import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  MessageSquare, 
  Receipt, 
  Mail, 
  Search,
  Loader2,
  Sparkles,
  Copy,
  Check
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAssistant = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  // Document Generator State
  const [docType, setDocType] = useState('contract');
  const [docContext, setDocContext] = useState('');

  // Case Summarizer State
  const [caseId, setCaseId] = useState('');
  const [caseNotes, setCaseNotes] = useState('');

  // Invoice Description State
  const [activityType, setActivityType] = useState('');
  const [hours, setHours] = useState('');
  const [invoiceContext, setInvoiceContext] = useState('');

  // Email Assistant State
  const [emailContext, setEmailContext] = useState('');
  const [emailTone, setEmailTone] = useState('professional');

  // Smart Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');

  const handleCopyResult = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 1. Generate Document
  const handleGenerateDocument = async () => {
    if (!docContext.trim()) {
      alert('Please enter document context');
      return;
    }

    setLoading(true);
    setResult('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/generate-document`,
        {
          document_type: docType,
          context: docContext,
          additional_params: {}
        },
        {
          withCredentials: true
        }
      );

      setResult(response.data.generated_content);
    } catch (error) {
      console.error('Error generating document:', error);
      alert('Error generating document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 2. Summarize Case
  const handleSummarizeCase = async () => {
    if (!caseId.trim() || !caseNotes.trim()) {
      alert('Please enter Case ID and notes to summarize');
      return;
    }

    setLoading(true);
    setResult('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/summarize-case`,
        {
          case_id: caseId,
          content_to_summarize: caseNotes
        },
        {
          withCredentials: true
        }
      );

      setResult(response.data.summary);
    } catch (error) {
      console.error('Error summarizing case:', error);
      alert('Error summarizing: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 3. Generate Invoice Description
  const handleGenerateInvoiceDescription = async () => {
    if (!activityType.trim() || !hours || !invoiceContext.trim()) {
      alert('Please fill all fields');
      return;
    }

    setLoading(true);
    setResult('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/ai/generate-invoice-description`,
        {
          activity_type: activityType,
          hours: parseFloat(hours),
          case_context: invoiceContext
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data.description);
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Error generating description: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 4. Email Assistant
  const handleEmailAssist = async () => {
    if (!emailContext.trim()) {
      alert('Please enter email context');
      return;
    }

    setLoading(true);
    setResult('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/assist-email`,
        {
          email_context: emailContext,
          tone: emailTone
        },
        {
          withCredentials: true
        }
      );

      setResult(response.data.suggested_email);
    } catch (error) {
      console.error('Error with email assist:', error);
      alert('Error with email assistance: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 5. Smart Search
  const handleSmartSearch = async () => {
    if (!searchQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    setLoading(true);
    setResult('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/smart-search`,
        {
          query: searchQuery,
          search_type: searchType
        },
        {
          withCredentials: true
        }
      );

      setResult(response.data.search_results);
    } catch (error) {
      console.error('Error with smart search:', error);
      alert('Search error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-600" />
            AI Assistant
          </h2>
          <p className="text-gray-600 mt-1">
            AI tools to boost your productivity
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Powered by GPT-4o
        </Badge>
      </div>

      {/* AI Tools Tabs */}
      <Tabs defaultValue="document" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="document">
            <FileText className="h-4 w-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="summarize">
            <MessageSquare className="h-4 w-4 mr-2" />
            Summaries
          </TabsTrigger>
          <TabsTrigger value="invoice">
            <Receipt className="h-4 w-4 mr-2" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="h-4 w-4 mr-2" />
            Search
          </TabsTrigger>
        </TabsList>

        {/* 1. Document Generator */}
        <TabsContent value="document">
          <Card>
            <CardHeader>
              <CardTitle>Legal Document Generator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Document Type</label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="memo">Memorandum</SelectItem>
                    <SelectItem value="clause">Clause</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Context & Requirements</label>
                <Textarea
                  value={docContext}
                  onChange={(e) => setDocContext(e.target.value)}
                  placeholder="Describe the document to generate (e.g., consulting agreement between ABC Ltd and XYZ Corp for IT services, 12 month duration...)"
                  rows={6}
                />
              </div>

              <Button 
                onClick={handleGenerateDocument} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Document
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. Case Summarizer */}
        <TabsContent value="summarize">
          <Card>
            <CardHeader>
              <CardTitle>Case Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Case ID</label>
                <Input
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  placeholder="Case ID to summarize"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes to Summarize</label>
                <Textarea
                  value={caseNotes}
                  onChange={(e) => setCaseNotes(e.target.value)}
                  placeholder="Paste the case notes you want to summarize..."
                  rows={8}
                />
              </div>

              <Button 
                onClick={handleSummarizeCase} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Summary
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. Invoice Description */}
        <TabsContent value="invoice">
          <Card>
            <CardHeader>
              <CardTitle>AI Invoice Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Activity Type</label>
                <Input
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  placeholder="e.g., Contract review, Consultation, Legal research..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Hours Worked</label>
                <Input
                  type="number"
                  step="0.5"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="3.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Case Context</label>
                <Textarea
                  value={invoiceContext}
                  onChange={(e) => setInvoiceContext(e.target.value)}
                  placeholder="Brief description of context (client, case, what was done...)"
                  rows={4}
                />
              </div>

              <Button 
                onClick={handleGenerateInvoiceDescription} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Description
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. Email Assistant */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Email Assistant</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tone</label>
                <Select value={emailTone} onValueChange={setEmailTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email Context</label>
                <Textarea
                  value={emailContext}
                  onChange={(e) => setEmailContext(e.target.value)}
                  placeholder="Describe what you want to communicate to the client (e.g., response to case update request, explain fee structure, confirm meeting...)"
                  rows={6}
                />
              </div>

              <Button 
                onClick={handleEmailAssist} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Email
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Smart Search */}
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle>Smart Search</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Search Type</label>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="cases">Cases Only</SelectItem>
                    <SelectItem value="clients">Clients Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Search Query</label>
                <Textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Semantic search (e.g., 'tech M&A cases in 2024', 'financial sector clients', 'pending commercial disputes'...)"
                  rows={4}
                />
              </div>

              <Button 
                onClick={handleSmartSearch} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Result Display */}
      {result && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>AI Result</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyResult}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-sm">
              {result}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIAssistant;
